/**
 * HotelCheckoutPage — prep mode only, at `/checkout/hotel/:tripId?items=id1,id2`.
 *
 * Runs `PrepareHotelCheckout` to fetch priced rooms, holds form state
 * client-side, submits via `SubmitHotelBooking`. Booking state lives directly
 * on each itinerary item; after submit the user is sent back to the trip's
 * bookings tab. The form, summary, status screens, and pure helpers live in
 * `./hotel-checkout/`.
 */

import type { Hotel } from '@elara/client-core/models/HotelModel';
import type { RoomOptionModel as RoomOption } from '@elara/client-core/models/RoomOptionModel';
import type { PartialBookingOutcome } from '@elara/client-core/states/HotelCheckoutState';
import type { HotelBookingRoom } from '@elara/domain/models/schemas/booking-schemas';

import type { RoomOutcome, RoomPriceChangeLine } from '#views/checkout/HotelBookingSummary.js';
import type { CheckoutSnapshot, HotelCheckoutFormState } from './hotel-checkout/types.js';

import React, { useEffect, useState } from 'react';

import { tripPanelPath } from '@elara/client-core/constants';
import { formatPrice } from '@elara/client-core/lib/utils';

import { useAppStates, useNavigate, useQueryParams, useRouteParams } from '#states/hooks.js';
import { CheckoutLayout } from '#views/checkout/CheckoutLayout.js';
import { HotelBookingSummary } from '#views/checkout/HotelBookingSummary.js';

import {
  AllUnavailableState,
  CheckoutErrorState,
  HotelBookingConfirmedState,
  PreparingState,
} from './hotel-checkout/CheckoutStates.js';
import { DraftForm } from './hotel-checkout/DraftForm.js';
import { computeLiveTotal, initialFormState, mergeProfileIntoForm } from './hotel-checkout/form-state.js';

export function HotelCheckoutPage(): React.ReactNode {
  const params = useRouteParams() as { tripId?: string };
  const query = useQueryParams();
  const tripId = params.tripId;
  const itemsParam = query.items;
  const itineraryItemIds = React.useMemo(() => (itemsParam ? itemsParam.split(',').filter(Boolean) : []), [itemsParam]);
  const { hotelCheckoutState, chatInputState } = useAppStates('hotelCheckoutState', 'chatInputState');
  const { navigate, goBack } = useNavigate();

  useEffect(() => {
    if (!tripId || itineraryItemIds.length === 0) return;
    void hotelCheckoutState.prepare(tripId, itineraryItemIds);
    return () => hotelCheckoutState.reset();
  }, [tripId, itineraryItemIds, hotelCheckoutState]);

  // Side-load related Firestore docs once prep data lands.
  useEffect(() => {
    if (!tripId || !hotelCheckoutState.isReady) return;
    hotelCheckoutState.hydrateRelated(tripId);
  }, [tripId, hotelCheckoutState, hotelCheckoutState.prepData?.hotelId, hotelCheckoutState.prepData?.rooms.length]);

  if (!tripId) {
    return <div className="p-8 text-sm text-destructive">Missing checkout parameters.</div>;
  }
  if (itineraryItemIds.length === 0) {
    return <div className="p-8 text-sm text-destructive">Missing items for checkout.</div>;
  }
  if (hotelCheckoutState.prepStatus === 'error') {
    return (
      <CheckoutErrorState
        message={hotelCheckoutState.prepError}
        onBack={() => {
          if (window.history.length > 1) goBack();
          else navigate(`/trip/${tripId}`);
        }}
      />
    );
  }
  if (!hotelCheckoutState.prepData) {
    return <PreparingState />;
  }

  const hotel = hotelCheckoutState.hotel;
  const roomOptionsById = hotelCheckoutState.roomOptionsById;
  /**
   * "Ask Elara" on a checkout notice: attaches the hotel, puts the drafted question in the composer (unsent,
   * so the traveler can add what they would accept instead) and opens the trip's chat — the same hand-off
   * as a guide's Ask Elara, with the question already written.
   */
  const askElara = (draft: string | undefined) =>
    draft === undefined
      ? undefined
      : () => {
          const hotelId = hotelCheckoutState.prepData?.hotelId;
          if (hotelId) chatInputState.addDataAttachment('hotel', hotelId, hotel?.name);
          chatInputState.queueDraft(draft);
          navigate(tripPanelPath(tripId, 'chat'));
          chatInputState.focusInput();
        };
  const snapshot: CheckoutSnapshot = {
    rooms: hotelCheckoutState.prepData.rooms,
    priceSnapshot: hotelCheckoutState.prepData.priceSnapshot,
    checkInDate: hotelCheckoutState.prepData.checkInDate,
    checkOutDate: hotelCheckoutState.prepData.checkOutDate,
    eligibleExtras: hotelCheckoutState.prepData.eligibleExtras ?? [],
  };
  // Unavailable rooms have already been split off into `droppedRooms` by
  // the state — they're surfaced as a banner in the summary, never enter
  // the form, and never reach SubmitHotelBooking.
  const droppedRoomNames = hotelCheckoutState.droppedRooms.map(
    (room) => roomOptionsById.get(room.roomOptionId)?.displayName ?? 'Room',
  );
  const droppedRoomsNote = hotelCheckoutState.droppedRoomsNote;
  // A room whose price moved stays in the checkout at the new figure; the summary shows old → new so the
  // traveler confirms knowingly instead of being told the room is gone.
  const priceChanges: RoomPriceChangeLine[] = hotelCheckoutState.priceChanges.map((change) => {
    const room = snapshot.rooms.find((r) => r.itineraryItemId === change.itineraryItemId);
    return {
      roomName: (room && roomOptionsById.get(room.roomOptionId)?.displayName) ?? 'Room',
      ...(change.previousTotal ? { previousTotal: change.previousTotal } : {}),
      ...(change.newTotal ? { newTotal: change.newTotal } : {}),
    };
  });

  // Every room came back unavailable — there's nothing for the user to
  // submit. Show a dedicated error state using the same single-column
  // layout as `PreparingState` so the page doesn't read as broken.
  if (snapshot.rooms.length === 0) {
    return (
      <AllUnavailableState
        droppedRoomNames={droppedRoomNames}
        onAskElara={askElara(hotelCheckoutState.droppedRoomsDraft)}
        onBack={() => {
          // Prefer the user's previous location; fall back to the trip's
          // default (chat) view when there's no history to return to
          // (e.g. checkout opened in a fresh tab).
          if (window.history.length > 1) goBack();
          else navigate(`/trip/${tripId}`);
        }}
      />
    );
  }

  return (
    <DraftView
      snapshot={snapshot}
      tripId={tripId}
      hotel={hotel}
      roomOptionsById={roomOptionsById}
      droppedRoomNames={droppedRoomNames}
      droppedRoomsNote={droppedRoomsNote}
      priceChanges={priceChanges}
      partialMessage={hotelCheckoutState.partialOutcome?.message}
      roomOutcomes={roomOutcomesFrom(hotelCheckoutState.partialOutcome, snapshot.rooms)}
      onAskElaraAboutDropped={askElara(hotelCheckoutState.droppedRoomsDraft)}
      onAskElaraAboutOutcome={askElara(hotelCheckoutState.partialOutcomeDraft)}
      onSubmitted={() => navigate(`/trip/${tripId}/bookings`)}
    />
  );
}

/**
 * Owns the draft form state and renders both checkout slots so the right-rail
 * summary can react live to selected extras without round-tripping
 * to the server.
 */
function DraftView({
  snapshot,
  tripId,
  hotel,
  roomOptionsById,
  droppedRoomNames,
  droppedRoomsNote,
  priceChanges,
  partialMessage,
  roomOutcomes,
  onAskElaraAboutDropped,
  onAskElaraAboutOutcome,
  onSubmitted,
}: {
  snapshot: CheckoutSnapshot;
  tripId: string;
  hotel: Hotel | undefined;
  roomOptionsById: Map<string, RoomOption | undefined>;
  droppedRoomNames: string[];
  droppedRoomsNote: string | undefined;
  priceChanges: RoomPriceChangeLine[];
  partialMessage: string | undefined;
  roomOutcomes: Record<string, RoomOutcome> | undefined;
  /** "Ask Elara" for the dropped-rooms notice in the summary, and for the partly-booked outcome screen. */
  onAskElaraAboutDropped: (() => void) | undefined;
  onAskElaraAboutOutcome: (() => void) | undefined;
  onSubmitted: () => void;
}): React.ReactNode {
  const { userProfileState, sessionState } = useAppStates('userProfileState', 'sessionState');
  const profile = userProfileState.profile;
  // The verified phone lives on the Firebase Auth user record (set by phone verification), NOT on the
  // Firestore profile — prefer it so a verified user's checkout phone pre-fills.
  const verifiedPhone = sessionState.user?.phoneNumber ?? undefined;

  const [form, setForm] = useState<HotelCheckoutFormState>(() => initialFormState(snapshot, profile, verifiedPhone));
  const [submitted, setSubmitted] = useState(false);

  // Re-merge profile defaults when the shape of the snapshot, the profile, or the
  // verified phone changes (e.g. any of them arrives async after first paint).
  useEffect(() => {
    setForm((prev) => mergeProfileIntoForm(prev, snapshot, profile, verifiedPhone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.rooms.length, profile?.email, profile?.phoneNumber, profile?.address?.line1, verifiedPhone]);

  // After a partly-booked order the money covers only what booked — in the collapsed header as well as the summary.
  const countedSnapshot = roomOutcomes
    ? {
        ...snapshot,
        priceSnapshot: snapshot.priceSnapshot.filter((item) => roomOutcomes[item.itineraryItemId] === 'booked'),
      }
    : snapshot;
  const liveTotal = computeLiveTotal(countedSnapshot);
  const liveTotalLabel = liveTotal ? formatPrice(liveTotal.amount, liveTotal.currency) : undefined;

  return (
    <CheckoutLayout
      summaryTotal={liveTotalLabel}
      form={
        submitted ? (
          <HotelBookingConfirmedState
            onBackToTrip={onSubmitted}
            partialMessage={partialMessage}
            onAskElara={onAskElaraAboutOutcome}
          />
        ) : (
          <div className="space-y-8">
            <DraftForm
              snapshot={snapshot}
              tripId={tripId}
              roomOptionsById={roomOptionsById}
              form={form}
              setForm={setForm}
              onSubmitted={() => setSubmitted(true)}
            />
          </div>
        )
      }
      summary={
        <HotelBookingSummary
          rooms={snapshot.rooms}
          priceSnapshot={snapshot.priceSnapshot}
          checkInDate={snapshot.checkInDate}
          checkOutDate={snapshot.checkOutDate}
          hotel={hotel}
          roomOptionsById={roomOptionsById}
          droppedRoomNames={droppedRoomNames}
          droppedRoomsNote={droppedRoomsNote}
          // Once the order is placed the summary is a record: no "confirm at the new price", and one Ask Elara
          // (the outcome's), not a second one for the rooms dropped before the form.
          onAskElara={submitted ? undefined : onAskElaraAboutDropped}
          priceChanges={submitted ? [] : priceChanges}
          roomOutcomes={roomOutcomes}
          formRooms={form.rooms}
          eligibleExtras={snapshot.eligibleExtras}
          liveTotal={liveTotal}
        />
      }
    />
  );
}

/**
 * Per-room outcome for the summary after a partly-booked order: booked, not booked (refused, gone, or never
 * attempted), or unconfirmed for the one room whose create may have gone through.
 */
function roomOutcomesFrom(
  outcome: PartialBookingOutcome | undefined,
  rooms: HotelBookingRoom[],
): Record<string, RoomOutcome> | undefined {
  if (!outcome) return;
  const booked = new Set(outcome.bookedItemIds);
  const outcomeOf = (itemId: string): RoomOutcome => {
    if (booked.has(itemId)) return 'booked';
    if (itemId === outcome.failedItemId && outcome.failedReason === 'unconfirmed') return 'unconfirmed';
    return 'notBooked';
  };
  return Object.fromEntries(rooms.map((room) => [room.itineraryItemId, outcomeOf(room.itineraryItemId)]));
}
